import { For, createSignal, onCleanup, onMount } from 'solid-js';
import { setSettings, settings, type Settings } from '../../hooks/useSettings';
import { useWorkspaces, type Workspace } from '../../indexeddb';
import { openFile } from '../../utils/files';
import { ctrlCmdIcon } from '../../utils/platform';
import MenuButton from './MenuButton';
import MenuDropdown from './MenuDropdown';
import MenuHeader from './MenuHeader';
import MenuSetting from './MenuSetting';

interface Props {
  onFileOpen?: (content: string) => void;
  onLoadFromURL?: (url: string) => void;
  onSave?: () => void;
  onSaveAll?: () => void;
  onRestore?: (workspace: Workspace) => void;
}

export default function Menu(props: Props) {
  const { workspaces } = useWorkspaces();
  const [openedMenu, setOpenedMenu] = createSignal<
    'file' | 'settings' | undefined
  >();
  let menuRef: HTMLUListElement | undefined;

  onMount(() => {
    document.addEventListener('click', onClickOutside);
  });

  onCleanup(() => {
    document.removeEventListener('click', onClickOutside);
  });

  function onClickOutside(event: MouseEvent) {
    if (menuRef && !menuRef.contains(event.target as Node)) {
      setOpenedMenu(undefined);
    }
  }

  return (
    <ul ref={menuRef} class="menu menu-horizontal bg-base-200 w-full">
      <MenuHeader
        title="文件"
        open={openedMenu() === 'file'}
        onOpen={() => setOpenedMenu('file')}
      >
        <MenuButton
          shortcut={[ctrlCmdIcon, 'O']}
          onClick={() => openFile(props.onFileOpen)}
        >
          打开文件…
        </MenuButton>
        <MenuButton
          onClick={() => {
            const url = prompt('Enter URL');
            if (url) {
              props.onLoadFromURL?.(url);
            }
          }}
        >
          从链接打开文件…
        </MenuButton>
        <MenuDropdown title="Open Recent">
          <For each={workspaces()} fallback={<li>暂无最近文件</li>}>
            {(workspace) => (
              <MenuButton
                class="whitespace-nowrap"
                onClick={() => props.onRestore?.(workspace)}
              >
                {new Date(workspace.timestamp).toLocaleString()} -
                <code class="overflow-x-clip text-ellipsis max-w-36">
                  {workspace.models[0].value.slice(0, 50)}
                </code>
              </MenuButton>
            )}
          </For>
        </MenuDropdown>
        <MenuButton shortcut={[ctrlCmdIcon, 'S']} onClick={props.onSave}>
          保存
        </MenuButton>
        <MenuButton onClick={props.onSaveAll}>全部保存 (.zip)</MenuButton>
      </MenuHeader>
      <MenuHeader
        title="设置"
        open={openedMenu() === 'settings'}
        onOpen={() => setOpenedMenu('settings')}
      >
        <MenuSetting>
          主题
          <select
            class="select select-sm ml-auto"
            value={settings.theme}
            onChange={(e) =>
              setSettings('theme', e.currentTarget.value as Settings['theme'])
            }
          >
            <option value="dark">夜间</option>
            <option value="light">白天</option>
            <option value="system">系统</option>
          </select>
        </MenuSetting>
        <MenuSetting>
          退出时确认
          <input
            type="checkbox"
            class="checkbox checkbox-sm ml-auto"
            checked={settings.confirmOnLeave}
            onChange={(e) =>
              setSettings('confirmOnLeave', e.currentTarget.checked)
            }
          />
        </MenuSetting>
        <MenuSetting>
          工作区历史记录
          <input
            type="checkbox"
            class="checkbox checkbox-sm ml-auto"
            checked={settings.workspaceHistory}
            onChange={(e) =>
              setSettings('workspaceHistory', e.currentTarget.checked)
            }
          />
        </MenuSetting>
        <MenuSetting>
          自动换行
          <input
            type="checkbox"
            class="checkbox checkbox-sm ml-auto"
            checked={settings.wordWrap}
            onChange={(e) => setSettings('wordWrap', e.currentTarget.checked)}
          />
        </MenuSetting>
        <MenuSetting>
          跟随滚动
          <input
            type="checkbox"
            class="checkbox checkbox-sm ml-auto"
            checked={settings.stickyScroll}
            onChange={(e) =>
              setSettings('stickyScroll', e.currentTarget.checked)
            }
          />
        </MenuSetting>
      </MenuHeader>
      <li>
        <a
          href="https://github.com/j4k0xb/webcrack"
          target="_blank"
          class="link link-hover"
        >
          GitHub
        </a>
      </li>
      <li>
        <a href="/docs" target="_blank" class="link link-hover">
          文档
        </a>
      </li>
    </ul>
  );
}
